import {
  DocumentData,
  DocumentSnapshot,
  QueryDocumentSnapshot,
  Timestamp,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  where
} from "firebase/firestore";
import { Keyword as FSKeyword, Index } from "../types";

import { Description } from "@mui/icons-material";
import { db } from "../firebase";

interface Keyword {
  text: string;
  position: number;  
  relevance: number;
}

const breakKeywords = (text: string, position: number): Keyword[] => {
  const keywords: Keyword[] = [];
  let source = text;
  let pass = 0;
  let relevance = 0;
  while (source.length > 1) {
    let word = "";
    for (const element of source) {
      word += element;
      if (pass > 0 && word.length === 1) {
        // do not include 1 character words from the 2nd character of text onwards
        continue;
      }
      if (!keywords.some((keyword) => keyword.text === word)) {
        keywords.push({ 
          text: word, 
          position: position,
          relevance: relevance
        });
        relevance++;
      }
      pass++;
    }
    source = source.slice(1);
  }
  return keywords;
};

const generateKeywords = (text: string): Keyword[] => {
  // Convert text to lowercase
  const lowerCaseText = text.toLowerCase();

  // Remove punctuation and special characters
  const textWithoutPunctuation = lowerCaseText.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "");

  // Split the text into an array of words
  const words = textWithoutPunctuation.split(/\s+/);

  // Remove stop words (common words that are not useful as keywords)
  const stopWords = ["the", "a", "an", "in", "on", "at", "to", "of", "for", "with", "is", "are", "was", "were", "be", "been", "being"];
  const filteredWords = words.filter(word => !stopWords.includes(word));

  const keywords: Keyword[] = [];
  for (let i = 0; i < filteredWords.length; i++) {
    const word = filteredWords[i];
    for (const kw of breakKeywords(word, i)) {
      keywords.push(kw);
    }
  }
  return keywords;
};

export const removeIndexesOf = async (document: string, field: string, documentPath: string) => {
  await removeIndexesOfV2(document, field, documentPath);
  // await removeIndexesOfV1(document, field, documentPath);
};

const deleteV2KeywordEntry = async (document: QueryDocumentSnapshot<DocumentData, DocumentData>) => {
  // const fsKeywordRef = document.ref.parent.parent;
  // if (fsKeywordRef) {
  //   const keywordSnap = await getDoc(fsKeywordRef);
  //   const keyword = keywordSnap.data() as FSKeyword;
  //   keyword.entriesCount -= 1;
  //   await setDoc(fsKeywordRef, keyword);  
  // }
  await deleteDoc(document.ref);
};

const removeIndexesOfV2 = async (document: string, field: string, documentPath: string) => {
  const indexesRef = collectionGroup(db, "keywordEntries");
  const q = query(indexesRef, where("document", "==", document), where("field", "==", field), where("documentPath", "==", documentPath));
  const querySnapshot = await getDocs(q);
  const promises: Promise<void>[] = [];
  querySnapshot.forEach((doc) => {
    promises.push(deleteV2KeywordEntry(doc));
  });
  await Promise.all(promises);
};

const removeIndexesOfV1 = async (document: string, field: string, documentPath: string) => {
  const indexesRef = collection(db, "indexes");
  const q = query(indexesRef, where("document", "==", document), where("field", "==", field), where("documentPath", "==", documentPath));
  const querySnapshot = await getDocs(q);
  const promises: Promise<void>[] = [];
  querySnapshot.forEach((doc) => {
    promises.push(deleteDoc(doc.ref));
  });
  await Promise.all(promises);
};

const addKeywordEntry = async (index: Index): Promise<boolean> => {
  let added: boolean = false;
  const keywordDocpath = `indexesV2/${index.document}/fields/${index.field}/keywords/${index.keyword}`;
  const keywordDocRef = doc(db, keywordDocpath);
  const keywordDocSnap = await getDoc(keywordDocRef);
  if (!keywordDocSnap.exists()) {
    await setDoc(keywordDocRef, {
      createdAt: Timestamp.now(),
      updatedBy: index.updatedBy
    });
  }
  const entryDocPath = `${keywordDocpath}/keywordEntries/${index.id}`;
  const entryDocRef = doc(db, entryDocPath);
  const entryDocSnap = await getDoc(entryDocRef);
  if (entryDocSnap.exists()) {
    const data = entryDocSnap.data() as Index;
    if (index.position < data.position) {
      await setDoc(entryDocRef, index);
    }
  } else {
    await setDoc(entryDocRef, index);
    added = true;
  }
  return added;
};

const updateFSKeyword = async (index: Index, isAdded: boolean, updatedBy: string) => {
  const keywordDocPath = `indexesV2/${index.document}/fields/${index.field}/keywords/${index.keyword}`;
  const keywordDocRef = doc(db, keywordDocPath);
  const keywordDocSnap = await getDoc(keywordDocRef);
  const keyword: FSKeyword = keywordDocSnap.exists() ?
    keywordDocSnap.data() as FSKeyword :
    {
      topEntries: [],
      entriesCount: 0,
      updatedAt: Timestamp.now(),
      updatedBy: updatedBy,
    };
  
  const indexesRef = collectionGroup(db, "keywordEntries");
  const q = query(indexesRef,
    limit(100),
    where("document", "==", index.document),
    where("field", "==", index.field),
    where("keyword", "==", index.keyword),
    orderBy("position", "asc"),
    orderBy("value", "asc"),
    orderBy("relevance", "asc")
  );
  const querySnapshot = await getDocs(q);
  keyword.topEntries = querySnapshot.docs.map(doc => doc.data() as Index);

  if (isAdded) {
    keyword.entriesCount += 1;
  }
  
  await setDoc(keywordDocRef, {
    ...keyword,
    updatedAt: Timestamp.now(),
    updatedBy: updatedBy,
  });
};

export const createIndex = async (document: string, field: string, fieldValue: string, documentPath: string, updatedBy: string) => {
  await createIndexV2(document, field, fieldValue, documentPath, updatedBy);
  // await createIndexV1(document, field, fieldValue, documentPath, updatedBy);
}

const createIndexV2 = async (document: string, field: string, fieldValue: string, documentPath: string, updatedBy: string) => {
  await removeIndexesOfV2(document, field, documentPath);
  const documentId = documentPath.split('/').slice(-1)[0];
  const keywords = generateKeywords(fieldValue);
  
  // Call addKeywordEntry in parallel
  const keywordPromises = keywords.map(async (keyword) => {
    const index: Index = {
      id: documentId,
      keyword: keyword.text,
      position: keyword.position,
      relevance: keyword.relevance,
      document: document,
      field: field,
      value: fieldValue,
      documentPath: documentPath,
      updatedAt: Timestamp.now(),
      updatedBy: updatedBy,
    };
    // const added = await addKeywordEntry(index);
    // await updateFSKeyword(index, added, updatedBy);
    await addKeywordEntry(index);
    console.debug("Added", index);
  });

  await Promise.all(keywordPromises);
}

const createIndexV1 = async (document: string, field: string, fieldValue: string, documentPath: string, updatedBy: string) => {
  await removeIndexesOfV1(document, field, documentPath);
  const keywords = generateKeywords(fieldValue);
  const indexesRef = collection(db, "indexes");
  for (const keyword of keywords) {
    const newIndexRef = doc(indexesRef);
    const index: Index = {
      id: newIndexRef.id,
      keyword: keyword.text,
      position: keyword.position,
      relevance: keyword.relevance,
      document: document,
      field: field,
      value: fieldValue,
      documentPath: documentPath,
      updatedAt: Timestamp.now(),
      updatedBy: updatedBy,
    };
    await setDoc(newIndexRef, index);
  }
};

export interface SearchParams {
  keywords: string[];
  resultLimit: number;
  document: string;
  field: string;
  searchAfter?: DocumentSnapshot<DocumentData, DocumentData> | null;
}

export type SearchResult = {
  indexes: Index[];
  lastSnapshotDoc: DocumentSnapshot<DocumentData, DocumentData> | null;
}

export const searchIndex = async (searchParams: SearchParams): Promise<SearchResult> => {
  if (searchParams.keywords.length === 0) {
    return {
      indexes: [],
      lastSnapshotDoc: null,
    };
  }

  const v2result = await searchIndexV2(searchParams);
  console.debug(`Search result for ${searchParams.keywords}`, v2result);
  return v2result;

  // if (v2result.indexes.length > 0) {
  //   return v2result;
  // }

  // const v1result = await searchIndexV1(searchParams);
  // return v1result;
};

const searchIndexV2 = async ({keywords, resultLimit, document, field, searchAfter}: SearchParams): Promise<SearchResult> => {
  const longestKeyword = keywords.reduce((a: string, b: string) => a.length > b.length ? a : b, '');
  const leadKeyword = longestKeyword.toLowerCase();
  const keywordsLowerCase = keywords.filter(kw => kw !== longestKeyword).map(kw => kw.toLowerCase());

  // const keywordDocPath = `indexesV2/${document}/fields/${field}/keywords/${leadKeyword}`;
  // const keywordDocRef = doc(db, keywordDocPath);
  // const keywordDocSnap = await getDoc(keywordDocRef);
  // if (!keywordDocSnap.exists()) {
  //   return {
  //     indexes: [],
  //     lastSnapshotDoc: null
  //   };
  // }

  let remaining = resultLimit;
  const searchIndexes: Index[] = [];

  const indexesRef = collectionGroup(db, "keywordEntries");
  let lastSnapshotDoc: DocumentSnapshot<DocumentData, DocumentData> | null = searchAfter ?? null;
  // if (!lastSnapshotDoc) {
  //   const keyword: FSKeyword = keywordDocSnap.data() as FSKeyword;
  //   let indexes = [...keyword.topEntries];
  //   for (const kw of keywordsLowerCase) {
  //     indexes = indexes.filter(index => index.value.toLowerCase().includes(kw));
  //     if (indexes.length === 0) {
  //       break;
  //     }
  //   }
  //   if (indexes.length > 0) {
  //     searchIndexes.push(...indexes);
  //   }
  //   const lastIndex = keyword.topEntries[keyword.topEntries.length - 1];
  //   const q = query(indexesRef,
  //     where("document", "==", lastIndex.document),
  //     where("field", "==", lastIndex.field),
  //     where("keyword", "==", lastIndex.keyword),
  //     where("id", "==", lastIndex.id),
  //     where("value", "==", lastIndex.value),
  //     where("documentPath", "==", lastIndex.documentPath),
  //     limit(1)
  //   );
  //   const querySnapshot = await getDocs(q);
  //   if (querySnapshot.docs.length > 0) {
  //     lastSnapshotDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
  //   }
  // }

  remaining -= searchIndexes.length;
  const page_size = keywords.length > 1 ? 100 : remaining;
  while (remaining > 0) {
    let q = query(indexesRef,
      limit(page_size),
      where("document", "==", document),
      where("field", "==", field),
      where("keyword", "==", leadKeyword),
      orderBy("position", "asc"),
      orderBy("value", "asc"),
      orderBy("relevance", "asc")
    );
    q = query(q, startAfter(lastSnapshotDoc));
    const querySnapshot = await getDocs(q);
    let indexes = querySnapshot.docs.map(doc => doc.data() as Index);
    if (indexes.length === 0) {
      break;
    }

    lastSnapshotDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
    for (const kw of keywordsLowerCase) {
      indexes = indexes.filter(index => index.value.toLowerCase().includes(kw));
      if (indexes.length === 0) {
        break;
      }
    }
    if (indexes.length > 0) {
      for (const index of indexes) {
        if (!searchIndexes.some(existingIndex => existingIndex.id === index.id)) {
          searchIndexes.push(index);
        }
      }
      remaining = resultLimit - searchIndexes.length;
    }
    if (searchIndexes.length >= resultLimit) {
      break;
    }
  }

  const searchResults: SearchResult = {
    indexes: searchIndexes,
    lastSnapshotDoc: lastSnapshotDoc
  }
  return searchResults;
};

const searchIndexV1 = async ({keywords, resultLimit, document, field, searchAfter}: SearchParams): Promise<SearchResult> => {
  let remaining = resultLimit;
  const searchIndexes: Index[] = [];
  const longestKeyword = keywords.reduce((a: string, b: string) => a.length > b.length ? a : b, '');
  const leadKeyword = longestKeyword.toLowerCase();
  const keywordsLowerCase = keywords.filter(kw => kw !== longestKeyword).map(kw => kw.toLowerCase());
  let lastSnapshotDoc: DocumentSnapshot<DocumentData, DocumentData> | null = searchAfter ?? null;

  while (remaining > 0) {
    const indexesRef = collection(db, "indexes");
    let q = query(indexesRef,
      limit(remaining),
      where("keyword", "==", leadKeyword),
      orderBy("position", "asc"),
      orderBy("relevance", "asc"),
    );
    if (document) {
      q = query(q, where("document", "==", document));
    }
    if (field) {
      q = query(q, where("field", "==", field));
    }
    if (lastSnapshotDoc) {
      q = query(q, startAfter(lastSnapshotDoc));
    }
    const querySnapshot = await getDocs(q);
    let indexes = querySnapshot.docs.map(doc => doc.data() as Index);
    if (indexes.length === 0) {
      break;
    }
    
    lastSnapshotDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
    
    for (const kw of keywordsLowerCase) {
      indexes = indexes.filter(index => index.value.toLowerCase().includes(kw));
      if (indexes.length === 0) {
        break;
      }
    }
    if (indexes.length > 0) {
      searchIndexes.push(...indexes);
      remaining = resultLimit - searchIndexes.length;
    }
    if (searchIndexes.length >= resultLimit) {
      break;
    }
  }
  
  const searchResults: SearchResult = {
    indexes: searchIndexes,
    lastSnapshotDoc: lastSnapshotDoc
  }
  return searchResults;
};

export const splitKeywords = (keywordText: string): string[] => {
  return keywordText
    .split(/\s+/)
    .map(keyword => 
      keyword.replace(/^[,.!\s]+|[,.!\s]+$/g, '')
    )
    .filter(keyword => keyword.length > 0);
};
